/**
 * คำสั่งหน้างานสามชนิดที่เครื่องสแกนสร้าง (D-075)
 *
 * หน้าจอหน้างาน **ไม่ POST ตรงไป server** — ทุกคำสั่งเข้าคิว IndexedDB ที่มีอยู่
 * แล้ว sync engine (Work Order แยก ยังไม่มีในบิลด์นี้) เป็นผู้ส่งขึ้น sync API
 * ภายหลัง ไฟล์นี้จึงเป็นที่เดียวที่รู้ว่า `command_type` ชื่ออะไร และ `payload`
 * มีคีย์อะไรบ้าง
 *
 * **`payload` = request body ของ endpoint นั้นตาม contract ที่ freeze แล้ว**
 * (`docs/API_CONTRACT.md`) ไม่ใช่รูปร่างที่หน้าจอคิดเอง — sync engine จะหยิบ
 * payload นี้ไปเป็น body ตรง ๆ ดังนั้นคีย์ที่ผิดชื่อวันนี้คือคำสั่งที่ถูกปฏิเสธ
 * ตอนซิงค์ในวันที่ผู้ปฏิบัติงานไม่ได้อยู่ตรงนั้นแล้ว
 *
 * ข้อแตกต่างเดียวจาก body ของ REST: **`production_order_id` อยู่ใน payload ของ
 * `STAGING_TRANSFER` ด้วย** ทั้งที่ contract วางไว้บน path
 * (`POST /v1/production-orders/{id}/staging-transfers`) เพราะคำสั่งที่นอนอยู่ใน
 * คิวไม่มี URL ติดมาด้วย — ถ้าไม่เก็บไว้ในตัวคำสั่ง sync engine จะไม่รู้ว่าใบไหน
 * (ตรงกับที่ Work Order ระบุ field ชุดนี้ไว้)
 *
 * สิ่งที่ **ห้าม** อยู่ใน payload:
 *   - `Idempotency-Key` / `client_event_id` / `device_sequence_no` — คิวเป็นผู้
 *     มินต์และจ่ายเองในธุรกรรมเดียวกับแถวที่บันทึก (`commandQueue.ts`) และ sync
 *     engine จะเป็นผู้ map `client_event_id` ไปเป็น `Idempotency-Key` ตอนส่ง
 *   - employee/device/credential ใด ๆ — ผู้กระทำจริงมาจาก auth context ฝั่ง
 *     server เสมอ (`started_by_employee_id`/`completed_by_employee_id` ตาม
 *     contract) ส่วน `employee_id` บน envelope เป็น attribution ไม่ใช่ตัวตน
 */

/** ย้าย lot เข้าจุด STAGING ข้างไลน์ + จองให้ order/operation (ledger-free, D-063) */
export const STAGING_TRANSFER = 'STAGING_TRANSFER';

/** เปิด cycle ที่ work center — ยังไม่ตัดสต๊อก (D-063/D-064) */
export const SCAN_IN = 'SCAN_IN';

/** ปิด cycle พร้อมสามจำนวน — server เป็นผู้ backflush/ลงบัญชี (D-065..D-070) */
export const SCAN_OUT = 'SCAN_OUT';

export type ShopFloorCommandType =
  | typeof STAGING_TRANSFER
  | typeof SCAN_IN
  | typeof SCAN_OUT;

/**
 * body ของ `POST /v1/production-orders/{id}/staging-transfers` + `production_order_id`
 *
 * `quantity` เป็น **string** เสมอ (contract ตอบ `400` ให้ JSON number)
 * `operation_seq` เป็น number ตามตัวอย่างใน contract
 */
export interface StagingTransferPayload {
  readonly plant_code: string;
  readonly production_order_id: string;
  readonly operation_seq: number;
  readonly product_code: string;
  readonly lot_id: string;
  readonly source_location_code: string;
  readonly staging_location_code: string;
  readonly quantity: string;
}

/** body ของ `POST /v1/shop-floor/scan-in` */
export interface ScanInPayload {
  readonly plant_code: string;
  readonly production_order_id: string;
  readonly operation_seq: number;
  readonly work_center_code: string;
  /** payload ดิบ `CAPTAG:1:MACHINE:...` ที่สแกนจากเครื่อง — ไม่ย่อเป็นรหัส เซิร์ฟเวอร์เป็นผู้ตรวจ */
  readonly machine_qr?: readonly string[];
}

/**
 * body ของ `POST /v1/shop-floor/scan-out` — สามจำนวนเป็น string ทั้งหมด
 *
 * `output_tag_code` เสริม (D-170, REV-009 F1) — บังคับเฉพาะ operation ที่มีป้าย
 * ACTIVE รอผลิต ซึ่งหน้าจอไม่รู้ล่วงหน้า จึงเป็น optional ที่นี่แล้วให้
 * เซิร์ฟเวอร์ตัดสินตอน sync
 */
export interface ScanOutPayload {
  readonly plant_code: string;
  readonly production_order_id: string;
  readonly operation_seq: number;
  readonly qty_good: string;
  readonly qty_scrap: string;
  readonly qty_rework: string;
  readonly output_tag_code?: string;
}

export type ShopFloorCommand =
  | { readonly command_type: typeof STAGING_TRANSFER; readonly payload: StagingTransferPayload }
  | { readonly command_type: typeof SCAN_IN; readonly payload: ScanInPayload }
  | { readonly command_type: typeof SCAN_OUT; readonly payload: ScanOutPayload };

/**
 * เขียนคีย์ทีละตัว — ไม่ spread
 *
 * ด่านที่สองต่อจากชนิดข้อมูล: ต่อให้ object ที่ส่งเข้ามามีคีย์แปลกปลอมติดมา (เช่น
 * state ของฟอร์มทั้งก้อน) สิ่งที่ลงไปนอนในคิวยังเป็นเฉพาะคีย์ที่ contract รับ
 * — เหตุผลเดียวกับ `serializeCreateGoodsReceiptBody()`
 *
 * จำนวนทุกตัวถูกคัดลอกเป็นค่าที่รับมาตรง ๆ ไม่ผ่าน `Number` ที่จุดใดเลย
 * `JSON.stringify` (ที่ IndexedDB structured clone และ sync engine จะเห็น) จึง
 * เห็นมันเป็นสตริงตามที่ผู้ปฏิบัติงานพิมพ์
 */
export function serializeShopFloorPayload(command: ShopFloorCommand): Record<string, unknown> {
  switch (command.command_type) {
    case STAGING_TRANSFER: {
      const { payload } = command;
      return {
        plant_code: payload.plant_code,
        production_order_id: payload.production_order_id,
        operation_seq: payload.operation_seq,
        product_code: payload.product_code,
        lot_id: payload.lot_id,
        source_location_code: payload.source_location_code,
        staging_location_code: payload.staging_location_code,
        quantity: payload.quantity,
      };
    }
    case SCAN_IN: {
      const { payload } = command;
      const result: Record<string, unknown> = {
        plant_code: payload.plant_code,
        production_order_id: payload.production_order_id,
        operation_seq: payload.operation_seq,
        work_center_code: payload.work_center_code,
      };
      /* คำสั่งเก่าในคิวที่ไม่มี machine_qr ต้องไม่เปลี่ยนรูป */
      if (payload.machine_qr !== undefined) {
        result.machine_qr = [...payload.machine_qr];
      }
      return result;
    }
    case SCAN_OUT: {
      const { payload } = command;
      const result: Record<string, unknown> = {
        plant_code: payload.plant_code,
        production_order_id: payload.production_order_id,
        operation_seq: payload.operation_seq,
        qty_good: payload.qty_good,
        qty_scrap: payload.qty_scrap,
        qty_rework: payload.qty_rework,
      };
      /* เฉพาะเมื่อมีค่า — คำสั่งเก่าในคิวต้องไม่เปลี่ยนรูป (ไม่ส่ง null/สตริงว่าง) */
      if (payload.output_tag_code !== undefined) {
        result.output_tag_code = payload.output_tag_code;
      }
      return result;
    }
  }
}
